import { Outlet } from 'react-router-dom';
import { Sidebar, Topbar, PageSuspense, Breadcrumbs, TermsLink, RouteErrorBoundary } from '@/shared/components';
import { useSession } from '@/shared/hooks/use-session';
import { useTenantQuery } from '@/modules/dashboard/hooks/use-tenant-query';
import { UpgradeProvider } from '@/shared/access';

/**
 * Standard dashboard chrome — sidebar on left, topbar on top, page content
 * fills the rest. Mirrors apps/dashboard/src/app/(dashboard)/layout.tsx.
 *
 * Differences from the legacy:
 *   - Session comes from `useSession()` (client-side cookie) instead of a
 *     server-side `getSession()`. ProtectedRoute already ensured a session
 *     exists by the time this layout mounts, but we still null-check for
 *     belt-and-braces during the brief race where the cookie expires while
 *     the layout is mid-render.
 *   - Tenant metadata is a query (5-min staleTime), not a layout-blocking
 *     fetch. Sidebar count badges were dropped because they fired five
 *     parallel list queries on every dashboard mount; each page now fetches
 *     only its own data.
 */
export function DashboardLayout() {
    const { session, isLoading: isSessionLoading } = useSession();
    const { data: tenant } = useTenantQuery();

    if (isSessionLoading) return null;
    if (!session) return null;

    const isAdmin = session.role === 'super_admin';
    const role = session.role;
    const subtype = isAdmin ? 'admin' : (tenant?.subtype ?? null);
    // Prefer the live tenant query (reflects a branding rename without a
    // re-login); fall back to the session cookie before the query resolves.
    const tenantName = isAdmin ? 'KMI Admin Platform' : (tenant?.name ?? session.tenantName);
    const userName = session.fullName;
    const userEmail = session.email;
    const isDevMode = session.isDevMode;

    return (
        <UpgradeProvider>
            <div className="flex h-screen bg-ink-50">
                <Sidebar
                    role={role}
                    subtype={subtype}
                    tenantName={tenantName}
                />
                <div className="flex-1 flex flex-col min-w-0">
                    <Topbar
                        tenantName={tenantName}
                        userName={userName}
                        userEmail={userEmail}
                        role={role}
                        subtype={subtype}
                        isDevMode={isDevMode}
                    />
                    <main className="flex-1 overflow-y-auto">
                        <div className="max-w-[1400px] mx-auto px-6 py-6">
                            <Breadcrumbs />

                            <RouteErrorBoundary>
                                <PageSuspense><Outlet /></PageSuspense>
                            </RouteErrorBoundary>
                            <footer className="mt-12 pt-4 border-t border-ink-100 text-[11px] text-ink-500 font-ui flex items-center justify-between">
                                <span>© {new Date().getFullYear()} KMI PropertyScan™</span>
                                <TermsLink>Terms & Conditions</TermsLink>
                            </footer>
                        </div>
                    </main>
                </div>
            </div>
        </UpgradeProvider>
    );
}
